// api/reportsApi.ts - Complete corrected version
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Interface pour les paramètres de rapport
interface TripSummaryParams {
  group_by: 'company' | 'driver';
  start?: string;
  end?: string;
}

interface TripDetailParams {
  start?: string;
  end?: string;
  driverId?: string;
  companyId?: string;
  truckId?: string;
  limit?: number;
  offset?: number;
}

interface ExportParams extends TripSummaryParams {
  format?: 'summary' | 'detail';
  driverId?: string;
  companyId?: string;
  truckId?: string;
}

interface FinancialSummaryParams {
  group_by: 'driver' | 'company' | 'month' | 'truck';
  start?: string;
  end?: string;
}

// Fonction utilitaire pour les appels API (uniquement pour les réponses JSON)
async function apiCall(endpoint: string, params?: Record<string, any>) {
  const url = new URL(`${API_BASE_URL}${endpoint}`);
  
  // Ajouter les paramètres à l'URL
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.append(key, value.toString());
      }
    });
  }

  // Récupérer le token depuis localStorage (utilisé par le frontend)
  const token = localStorage.getItem('token');
  
  console.log('Making API call to:', url.toString());
  console.log('Token present:', !!token);
  
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    },
  });

  console.log('Response status:', response.status);

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
    }
    console.error('API Error:', errorData);
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  const data = await response.json();
  console.log('API Response:', data);
  return data;
}

// Fonction utilitaire pour les téléchargements de fichiers
async function downloadFile(endpoint: string, params: Record<string, any>, defaultFilename: string) {
  const url = new URL(`${API_BASE_URL}${endpoint}`);
  
  // Ajouter les paramètres à l'URL
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.append(key, value.toString());
    }
  });

  const token = localStorage.getItem('token');
  
  console.log('Making file download request to:', url.toString());
  
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
    },
  });

  console.log('Response status:', response.status);
  console.log('Content-Type:', response.headers.get('content-type'));

  if (!response.ok) {
    // Pour les erreurs, essayer de parser en JSON
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
    }
    console.error('Download Error:', errorData);
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  // Pour les réponses réussies, traiter comme blob
  const blob = await response.blob();
  
  // Récupérer le nom de fichier depuis l'en-tête Content-Disposition
  const contentDisposition = response.headers.get('content-disposition');
  let filename = defaultFilename;
  
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }
  
  // Créer le lien de téléchargement et déclencher le téléchargement
  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(downloadUrl);
  
  console.log('File downloaded successfully:', filename);
  return { success: true, filename };
}

/**
 * Récupère le rapport de synthèse des courses
 */
export const getTripsSummary = async (params: TripSummaryParams) => {
  try {
    console.log('Calling getTripsSummary with params:', params);
    
    // Essayer d'abord la version ORM plus stable
    const data = await apiCall('/api/reports/trips-summary-orm', params);
    
    if (!data.success) {
      throw new Error(data.error || 'Erreur lors de la génération du rapport');
    }
    
    return data;
  } catch (error) {
    console.error('getTripsSummary ORM error:', error);
    
    // Fallback vers la version SQL raw si l'ORM échoue
    try {
      console.log('Trying fallback to raw SQL version...');
      const fallbackData = await apiCall('/api/reports/trips-summary', params);
      
      if (!fallbackData.success) {
        throw new Error(fallbackData.error || 'Erreur lors de la génération du rapport (fallback)');
      }
      
      return fallbackData;
    } catch (fallbackError) {
      console.error('Fallback also failed:', fallbackError);
      throw error; // Relancer l'erreur originale
    }
  }
};

/**
 * Récupère les détails des courses
 */
export const getTripsDetail = async (params: TripDetailParams = {}) => {
  try {
    console.log('Calling getTripsDetail with params:', params);
    
    const data = await apiCall('/api/reports/trips-detail', params);
    
    if (!data.success) {
      throw new Error(data.error || 'Erreur lors de la récupération des détails');
    }
    
    return data;
  } catch (error) {
    console.error('getTripsDetail error:', error);
    throw error;
  }
};

/**
 * Exporte les données au format CSV (retourne JSON)
 */
export const exportTripsCSV = async (params: ExportParams) => {
  try {
    console.log('Calling exportTripsCSV with params:', params);
    const data = await apiCall('/api/reports/trips-export', params);
    if (!data.success) throw new Error(data.error || "Erreur lors de l'export");
    return data;
  } catch (error) {
    console.error('exportTripsCSV error:', error);
    throw error;
  }
};

/**
 * Exporte les données des courses dans un fichier (CSV/XLSX/PDF)
 */
export const exportTripsFile = async (
  params: ExportParams,
  file: 'csv'|'xlsx'|'pdf'
) => {
  const fileParams = { ...params, file };
  const defaultFilename = `trips-${params.format || 'summary'}.${file}`;
  return downloadFile('/api/reports/trips-export', fileParams, defaultFilename);
};

/**
 * Récupère le résumé financier
 */
export const getFinancialSummary = async (params: FinancialSummaryParams) => {
  try {
    console.log('Calling getFinancialSummary with params:', params);
    const data = await apiCall('/api/reports/financial', params);
    
    if (!data.success) {
      throw new Error(data.error || 'Erreur lors de la génération du rapport financier');
    }
    
    return data;
  } catch (error) {
    console.error('getFinancialSummary error:', error);
    throw error;
  }
};



/**
 * Exporte le rapport financier dans un fichier (CSV/XLSX/PDF)
 */
export const exportFinancialFile = async (
  params: FinancialSummaryParams,
  format: 'csv' | 'xlsx' | 'pdf'
) => {
  const fileParams = { ...params, format };
  const defaultFilename = `financial-summary.${format}`;
  return downloadFile('/api/reports/financial-export', fileParams, defaultFilename);
};

/**
 * Fonction de test pour vérifier la connectivité de l'API
 */
export const testReportsAPI = async () => {
  try {
    // Test de connectivité basique
    const healthCheck = await fetch(`${API_BASE_URL}/health`);
    if (!healthCheck.ok) {
      throw new Error('Service non disponible');
    }
    
    // Test simple avec des paramètres minimaux
    const testParams: TripSummaryParams = {
      group_by: 'company'
    };
    
    const result = await getTripsSummary(testParams);
    console.log('API test successful:', result);
    return { success: true, data: result };
  } catch (error) {
    console.error('API test failed:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};